/**
 * claude-regional-fixes (issue 3) — explicit per-region setup for a parallel
 * multi-region submission.
 *
 * A parallel group shares product/company metadata only; every region is a
 * DISTINCT regulatory application and must declare its OWN application type,
 * activity code and market identifier. This component renders one setup card
 * per selected additional region (the primary region is driven by the main
 * wizard form), fetching each region's SubmissionTypesCatalog so the dropdowns
 * can only offer values that region actually recognises. Nothing is copied or
 * invented across regions.
 */
import { useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import { v2, type SubmissionTypeGroup } from '../../api/v2Client';
import {
  emptyRegionDraft,
  parallelActivityStartError,
  identifierSpecForRegion,
  isRegionDraftComplete,
  type ParallelRegionDraft,
} from './parallelRegionSetup';

interface RegionCatalog {
  appTypes: string[];
  byAppType: Map<string, SubmissionTypeGroup[]>;
  loading: boolean;
}

const cardStyle: CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '12px 14px',
  marginTop: 10,
  background: 'var(--surface)',
};
const fieldRow: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 };
const labelStyle: CSSProperties = { fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' };

interface Props {
  readonly regions: string[];
  readonly value: Record<string, ParallelRegionDraft>;
  readonly onChange: (region: string, draft: ParallelRegionDraft) => void;
  readonly showErrors: boolean;
  readonly flags?: Readonly<Record<string, string>>;
}

export function ParallelRegionSetups({ regions, value, onChange, showErrors, flags }: Props): ReactElement {
  const [catalogs, setCatalogs] = useState<Record<string, RegionCatalog>>({});

  // Fetch each selected region's catalog once (by region membership).
  useEffect(() => {
    let cancelled = false;
    for (const region of regions) {
      if (catalogs[region] && !catalogs[region].loading) continue;
      setCatalogs((prev) => ({
        ...prev,
        [region]: { appTypes: [], byAppType: new Map(), loading: true },
      }));
      void v2.submissionTypes
        .list({ region })
        .then(async (res) => {
          const appTypes = res.applicationTypes;
          const entries = await Promise.all(
            appTypes.map((at) =>
              v2.submissionTypes
                .list({ region, applicationType: at })
                .then((r) => [at, r.submissionTypes] as const),
            ),
          );
          if (cancelled) return;
          const byAppType = new Map<string, SubmissionTypeGroup[]>(entries);
          setCatalogs((prev) => ({ ...prev, [region]: { appTypes, byAppType, loading: false } }));
        })
        .catch(() => {
          if (cancelled) return;
          setCatalogs((prev) => ({
            ...prev,
            [region]: { appTypes: [], byAppType: new Map(), loading: false },
          }));
        });
    }
    return (): void => {
      cancelled = true;
    };
    // `catalogs` intentionally omitted: we fetch per NEW region only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions]);

  if (regions.length === 0) return <></>;

  return (
    <div data-testid="parallel-region-setups" style={{ marginTop: 10 }}>
      <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Additional regions start new applications. Create supplements from each existing application.</p>
      {regions.map((region) => {
        const draft = value[region] ?? emptyRegionDraft();
        const catalog = catalogs[region];
        const groups = draft.applicationType
          ? catalog?.byAppType.get(draft.applicationType)?.filter(group => !group.requiresPriorDocumentSet)
          : undefined;
        const selectedGroup = groups?.find((g) => g.code === draft.submissionTypeCode);
        const idLabel = identifierSpecForRegion(region)?.label ?? 'Application identifier';
        const set = (patch: Partial<ParallelRegionDraft>): void =>
          onChange(region, { ...draft, ...patch });
        const startError = showErrors ? parallelActivityStartError(region, draft) : null;
        const incomplete = showErrors && !isRegionDraftComplete(draft, groups);

        return (
          <div
            key={region}
            data-testid={`parallel-setup-${region}`}
            style={{ ...cardStyle, ...(incomplete ? { borderColor: 'var(--red)' } : {}) }}
          >
            <div style={{ fontWeight: 600, fontSize: 13 }}>
              {flags?.[region] ?? ''} {region}
            </div>

            <div style={fieldRow}>
              <label style={labelStyle} htmlFor={`pr-apptype-${region}`}>
                Application type
              </label>
              <select
                id={`pr-apptype-${region}`}
                data-testid={`parallel-apptype-${region}`}
                value={draft.applicationType}
                disabled={catalog?.loading ?? true}
                onChange={(e) =>
                  set({ applicationType: e.target.value, submissionTypeCode: '', submissionSubTypeCode: '' })
                }
              >
                <option value="">{catalog?.loading ? 'Loading…' : 'Select application type…'}</option>
                {(catalog?.appTypes ?? []).map((at) => (
                  <option key={at} value={at}>
                    {at}
                  </option>
                ))}
              </select>
            </div>

            {draft.applicationType && (
              <div style={fieldRow}>
                <label style={labelStyle} htmlFor={`pr-type-${region}`}>
                  Submission type
                </label>
                <select
                  id={`pr-type-${region}`}
                  data-testid={`parallel-type-${region}`}
                  value={draft.submissionTypeCode}
                  onChange={(e) => set({ submissionTypeCode: e.target.value, submissionSubTypeCode: '' })}
                >
                  <option value="">Select submission type…</option>
                  {(groups ?? []).map((g) => (
                    <option key={g.code} value={g.code}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedGroup && selectedGroup.subTypes.length > 0 && (
              <div style={fieldRow}>
                <label style={labelStyle} htmlFor={`pr-subtype-${region}`}>
                  Sub-type
                </label>
                <select
                  id={`pr-subtype-${region}`}
                  data-testid={`parallel-subtype-${region}`}
                  value={draft.submissionSubTypeCode}
                  onChange={(e) => set({ submissionSubTypeCode: e.target.value })}
                >
                  <option value="">Select sub-type…</option>
                  {selectedGroup.subTypes.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={fieldRow}>
              <label style={labelStyle} htmlFor={`pr-id-${region}`}>
                {idLabel}
              </label>
              <input
                id={`pr-id-${region}`}
                data-testid={`parallel-identifier-${region}`}
                value={draft.identifier}
                placeholder={idLabel}
                onChange={(e) => set({ identifier: e.target.value })}
              />
            </div>

            {region === 'FDA' && <label style={fieldRow}>
              FDA activity starting sequence
              <input value={draft.fdaActivityStartSequence ?? ''} inputMode="numeric" maxLength={4}
                aria-invalid={startError ? true : undefined}
                onChange={event => set({ fdaActivityStartSequence: event.target.value })} placeholder="0000" />
              {startError && <span role="alert" style={{ color: 'var(--red)' }}>{startError}</span>}
              <span style={{ fontSize: 12 }}>Enter the sequence where this activity began before compilation.</span>
            </label>}

            {incomplete && (
              <p role="alert" style={{ color: 'var(--red)', fontSize: 12, margin: '8px 0 0' }}>
                Set this region&rsquo;s application type, submission type{selectedGroup && selectedGroup.subTypes.length > 0 ? ', sub-type' : ''} and {idLabel.toLowerCase()}.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
